import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { ICrisisPlan } from '../../utils/types';

const name = 'crisisPlans';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<CrisisPlanState>) =>
    createExtraReducers(builder),
});

export interface CrisisPlanState {
  treatmentPlanId: number | null;
  current: ICrisisPlan | null;
  loading: boolean;
  error: any;
}

type CrisisPlanStateUpdate = {
  [Key in keyof CrisisPlanState]: { key: Key; data: CrisisPlanState[Key] };
}[keyof CrisisPlanState];

function createInitialState(): CrisisPlanState {
  return {
    treatmentPlanId: null,
    current: null,
    loading: false,
    error: null,
  };
}

function createReducers() {
  return {
    updateEditing: updateEditing,
    updateState: updateState,
  };

  function updateState(state: CrisisPlanState, action: PayloadAction<CrisisPlanStateUpdate>) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }

  function updateEditing(state: CrisisPlanState, action: any) {
    const { data } = action.payload;
    state.current = state.current ? { ...state.current, ...data } : data;
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getCrisisPlan: getCrisisPlan(),
    createCrisisPlan: createCrisisPlan(),
    updateCrisisPlan: updateCrisisPlan(),
    removeCrisisPlan: removeCrisisPlan(),
  };

  function getCrisisPlan() {
    return createAsyncThunk(
      `${name}/getCrisisPlan`,
      async ({ treatmentPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${treatmentPlanId}/crisis_plans/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createCrisisPlan() {
    return createAsyncThunk(
      `${name}/createCrisisPlan`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${treatmentPlanId}/crisis_plans/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateCrisisPlan() {
    return createAsyncThunk(
      `${name}/updateCrisisPlan`,
      async ({ treatmentPlanId, data, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${treatmentPlanId}/crisis_plans/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function removeCrisisPlan() {
    return createAsyncThunk(
      `${name}/removeCrisisPlan`,
      async ({ treatmentPlanId, crisisPlanId, getAccessTokenSilently }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${treatmentPlanId}/crisis_plans/${crisisPlanId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<CrisisPlanState>) {
  const { getCrisisPlan, createCrisisPlan, updateCrisisPlan, removeCrisisPlan } = extraActions;

  builder
    // getCrisisPlan()
    .addCase(getCrisisPlan.pending, (state: CrisisPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(getCrisisPlan.fulfilled, (state: CrisisPlanState, _action: any) => {
      state.loading = false;
      state.current = null;
    })
    .addCase(getCrisisPlan.rejected, (state: CrisisPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
    })

    // createCrisisPlan()
    .addCase(createCrisisPlan.pending, (state: CrisisPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createCrisisPlan.fulfilled, (state: CrisisPlanState, action: any) => {
      state.loading = false;
      state.current = action.payload;
    })
    .addCase(createCrisisPlan.rejected, (state: CrisisPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // updateCrisisPlan()
    .addCase(updateCrisisPlan.pending, (state: CrisisPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateCrisisPlan.fulfilled, (state: CrisisPlanState, _action: any) => {
      state.loading = false;
      state.current = null;
    })
    .addCase(updateCrisisPlan.rejected, (state: CrisisPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })

    // removeCrisisPlan()
    .addCase(removeCrisisPlan.pending, (state: CrisisPlanState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(removeCrisisPlan.fulfilled, (state: CrisisPlanState, _action: any) => {
      state.loading = false;
      state.current = null;
    })
    .addCase(removeCrisisPlan.rejected, (state: CrisisPlanState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    });
}

export const crisisPlanActions = { ...slice.actions, ...extraActions };
export const crisisPlanReducer = slice.reducer;
